import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { Snackbar, Alert, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';

// PR-UX-001 — the one shared toast/snackbar primitive for confirming that a user-initiated,
// state-changing action succeeded or failed. Several pages already hand-roll an equivalent
// (local `{ open, message, severity }` state + modules/communication/components/Notification.js,
// re-declared per page) but many core lifecycle screens — Offers, Bench, Interviews, Placements,
// Consultant — had none at all, so a successful or failed click gave the user zero confirmation
// (V1 UI/UX Audit, P0/P1). This component doesn't replace or migrate that existing pattern (out of
// scope here — a mechanical follow-up, not a design change); it gives every *new* call site one
// global instance to reuse instead of inventing a 23rd local copy.
//
// Usage — mount once near the app root (see App.js), then anywhere beneath it:
//   const { showSuccess, showError } = useToast();
//   try { await action(); showSuccess('Offer sent successfully.'); }
//   catch (error) { showError(getErrorMessage(error)); }
//
// Falls back to a no-op if no <ToastProvider> is mounted, so existing page-level tests that
// render a page in isolation (no provider in the tree) keep working unchanged.
const noop = () => {};
const ToastContext = createContext({
  showSuccess: noop,
  showError: noop,
  showWarning: noop,
  showInfo: noop,
});

export const useToast = () => useContext(ToastContext);

// Matches the auto-hide convention already established by Notification.js — errors stay up
// longer since there's more to read, everything auto-dismisses so a toast never has to be
// manually cleared to keep working.
const AUTO_HIDE_MS = { success: 4000, info: 4000, warning: 5000, error: 6000 };

export const ToastProvider = ({ children }) => {
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });

  const show = useCallback((message, severity) => {
    setToast({ open: true, message, severity });
  }, []);

  const handleClose = useCallback((_event, reason) => {
    if (reason === 'clickaway') return;
    setToast((t) => ({ ...t, open: false }));
  }, []);

  const value = useMemo(() => ({
    showSuccess: (message) => show(message, 'success'),
    showError: (message) => show(message, 'error'),
    showWarning: (message) => show(message, 'warning'),
    showInfo: (message) => show(message, 'info'),
  }), [show]);

  return (
    <ToastContext.Provider value={value}>
      {children}
      <Snackbar
        open={toast.open}
        autoHideDuration={AUTO_HIDE_MS[toast.severity] ?? 4000}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          severity={toast.severity}
          variant="filled"
          sx={{ width: '100%' }}
          action={
            <IconButton
              size="small"
              color="inherit"
              aria-label="Close notification"
              onClick={handleClose}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          }
        >
          {toast.message}
        </Alert>
      </Snackbar>
    </ToastContext.Provider>
  );
};

export default ToastProvider;
